/* eslint-disable no-shadow */

import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError } from '@/errors';
import axios from 'axios';

const endpoint = `${apiEndpoint}/keywords-trans`;

const state = {
  projectKeys: null,
  pending: false,
  error: null,
  updatePending: false,
  updateError: null,
  totalCount: null,
  projectPage: null,
  project: null,
  activeProject: null,
  activeLanguage: null,
  deepLExcludedLangs: null,
};

const getters = {
  loaded: (state) => !state.pending && state.projectKeys != null,
  projectKeys: (state, getters) => (getters.loaded ? state.projectKeys : []),
  noProjectKeys: (state, getters) => getters.loaded && !getters.projectKeys.length && state.totalCount === 0,
  activeProject: (state) => state.activeProject ?? null,
  activeLanguage: (state) => state.activeLanguage ?? null,
  deepLExcludedLangs: (state) => state.deepLExcludedLangs ?? [],
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.projectKeys = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, {
    projectKeys, totalCount, projectPage, project,
  }) {
    state.pending = false;
    state.projectKeys = projectKeys;
    state.totalCount = totalCount;
    state.projectPage = projectPage;
    state.project = project;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state) {
    state.updatePending = false;
    state.updateError = null;
  },
  modified(state) {
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, pid) {
    const idx = state.projectKeys.findIndex((projectKey) => projectKey.id === pid);
    if (idx !== -1) {
      state.projectKeys.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
  activeProject(state, projectId) {
    state.activeProject = projectId;
  },
  activeLanguage(state, languageIso) {
    state.activeLanguage = languageIso;
  },
  deepLExcludedLangs(state, deepLExcludedLangs) {
    state.deepLExcludedLangs = deepLExcludedLangs;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      if (data.errors) {
        data.message = data.errors[Object.keys(data.errors)[0]];
      }
      err = new ValidationError(status, data.message, data.errors);
      break;
    default:
      break;
  }
  if (error.response && error.response.data) {
    err = error.response.data;
  }
  return err;
}

const actions = {
  async fetchData({ commit }, payload) {
    commit('beforeLoading');
    try {
      const result = await ApiService.get(`${endpoint}?currentPage=${payload.currentPage}&projectPageId=${payload.projectPageId}&languageId=${payload.languageId}&withoutTranslations=${payload.withoutTranslations}&q=${payload.q}`);
      let projectKeys = result.data.data.items;
      // const regex = /(<([^>]+)>)/ig;
      const mapItem = (item) => {
        const newItem = { ...item };
        // newItem.richtextField = !!regex.test(item.translation.translation);
        return newItem;
      };

      if (Array.isArray(projectKeys)) {
        projectKeys = projectKeys.map(mapItem);
      } else {
        projectKeys = Object.values(projectKeys).map((item) => mapItem(item));
      }
      const totalCount = result.data.data.totalRows;
      const { projectPage } = result.data.data;
      const { project } = result.data.data;
      commit('data', {
        projectKeys, totalCount, projectPage, project,
      });
    } catch (error) {
      commit('error', error);
    }
  },
  async fetchAllTranslations({ commit }, payload) {
    commit('beforeLoading');
    try {
      const result = await ApiService.get(`${endpoint}/translations/all?currentPage=${payload.currentPage}&projectId=${payload.projectId}&languageId=${payload.languageId}&withoutTranslations=${payload.withoutTranslations}&q=${payload.q}&deepL=false&toBeChecked=${payload.toBeChecked}`);
      let projectKeys = result.data.data.items;
      // const regex = /(<([^>]+)>)/ig;
      const mapItem = (item) => {
        const newItem = { ...item };
        // newItem.richtextField = !!regex.test(item.translation.translation);
        return newItem;
      };

      if (Array.isArray(projectKeys)) {
        projectKeys = projectKeys.map(mapItem);
      } else {
        projectKeys = Object.values(projectKeys).map((item) => mapItem(item));
      }
      const totalCount = result.data.data.totalRows;
      const { project } = result.data.data;
      const projectPage = {
        name: '',
      };
      commit('data', {
        projectKeys, totalCount, projectPage, project,
      });
    } catch (error) {
      commit('error', error);
    }
  },
  async createProjectTranslation({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}`, { ...payload });
      if (payload.language_iso === 'en') {
        // axios.get(`${apiEndpoint}/updateAfterEnglish?keyword_id=${payload.keyword_id}`);
      }
      commit('created', data);
      return data.data;
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateProjectTranslation({ commit }, projectKey) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${projectKey.id}`, { ...projectKey });
      commit('modified', { ...projectKey });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteProjectTranslation({ commit }, pid) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/${pid}`);
      commit('deleted', pid);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async getToBeChecked({ commit }, projectId) {
    try {
       const result = await axios.get(`${apiEndpoint}/getToBeChecked?projectId=${projectId}`);
       return result.data;
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async generateKeywordImage({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${apiEndpoint}/screenshot`, { ...payload });
      commit('created', data);
      return data.data;
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async uploadKeywordImage({ commit }, formData) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${apiEndpoint}/screenshot`, formData);
      commit('created', data);
      return data.data;
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async removeKeywordImage({ commit }, id) {
    commit('beforeUpdate');
    try {
       await ApiService.delete(`${apiEndpoint}/screenshot/${id}`);
       commit('modified', id);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateSearchResults({ commit }, payload) {
    commit('beforeLoading');
    try {
      const projectKeys = [payload];
      const { totalCount } = state;
      const { projectPage } = state;
      const { project } = state;
      commit('data', {
        projectKeys, totalCount, projectPage, project,
      });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateActiveProjectAndLanguage({ commit }, payload) {
    try {
      commit('activeProject', payload.projectId);
      commit('activeLanguage', payload.languageIso);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deepLExcludedLanguages({ commit }) {
    try {
      const { data } = await ApiService.get(`${apiEndpoint}/deepl-excluded-langs`);
      commit('deepLExcludedLangs', data.data);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async specificSearchResult({ commit }, payload) {
    commit('beforeLoading');
    try {
      const queryString = new URLSearchParams(payload).toString();
      const result = await ApiService.get(`${apiEndpoint}/specificTranslation?${queryString}`);
      const projectKeys = result.data.data.items;

      const totalCount = result.data.data.totalRows;
      const { project } = result.data.data;
      const projectPage = {
        name: '',
      };
      commit('data', {
        projectKeys, totalCount, projectPage, project,
      });
    } catch (error) {
      commit('error', error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
